package fr.frux.core.ads

import kotlinx.coroutines.flow.Flow

/**
 * Port vers le SDK de la régie (ADR 0030, #527). Toute la politique (session, droits, consentement,
 * arbitrage) vit dans le code commun, ce port ne fait qu'exécuter. Android : GMA et UMP (#527 T3).
 * Web, desktop et iOS avant le jalon M4 : [NoAdsPlatform].
 *
 * Aucune méthode n'est appelée tant que le contrôleur n'a pas établi que la personne peut voir de la
 * publicité : ni en première session, ni pour un compte sans pub, ni sous un hold d'onboarding.
 */
interface AdsPlatform {
    /** `false` : plateforme sans publicité, le contrôleur n'appelle alors rien d'autre. */
    val supported: Boolean

    /**
     * Process au premier plan. Android : `ProcessLifecycleOwner`, sur lequel l'Activity de la régie
     * (`AdActivity`, même process) ne compte pas comme un passage en arrière-plan.
     */
    val foreground: Flow<Boolean>

    /** Mise à jour silencieuse de l'état de consentement (UMP `requestConsentInfoUpdate`). */
    suspend fun requestConsentInfo(): ConsentInfo

    /** Affiche le formulaire de consentement s'il est requis, rend `canRequestAds` à sa fermeture. */
    suspend fun showConsentForm(): Boolean

    /** Formulaire des choix publicitaires (entrée Profil), rend `canRequestAds` à sa fermeture. */
    suspend fun showPrivacyOptions(): Boolean

    /** Initialisation du SDK, appelée seulement quand `canRequestAds` est vrai. */
    suspend fun initialize()

    /**
     * Charge un interstitiel en tâche de fond (#528), appelée seulement une fois le SDK initialisé et
     * la publicité permise. `true` si une annonce est prête à être montrée. Un échec est une exception
     * ou `false` : le contrôleur n'insiste pas avant la session suivante.
     */
    suspend fun preloadInterstitial(): Boolean

    /**
     * Montre l'interstitiel préchargé, sans rien attendre (#528). `false` si aucune annonce prête, si elle
     * a expiré ou s'il n'y a pas d'Activity au premier plan. [onClosed] est appelé une seule fois, à
     * la fermeture de l'annonce ou si son affichage échoue, jamais quand le résultat est `false`.
     */
    fun showInterstitial(onClosed: () -> Unit): Boolean

    /**
     * Charge une vidéo récompensée au moment du geste de la personne (#531), jamais à l'avance.
     * `false` si rien n'a pu être chargé (pas de bloc configuré, pas d'annonce).
     */
    suspend fun loadRewarded(): Boolean

    /**
     * Montre la vidéo chargée par [loadRewarded] avec les options SSV de [request], que Google relaie
     * au serveur dans son rappel signé. Sans vidéo chargée ni Activity au premier plan :
     * [RewardedOutcome.UNAVAILABLE], sans attendre.
     */
    suspend fun showRewarded(request: RewardedRequest): RewardedOutcome
}

data class ConsentInfo(
    val formRequired: Boolean,
    val canRequestAds: Boolean,
    val privacyOptionsRequired: Boolean,
)

/** Options SSV d'une vidéo récompensée (#531) : `ServerSideVerificationOptions(userId, customData = rewardKind)`. */
data class RewardedRequest(
    val userId: String,
    val rewardKind: String,
)

/**
 * Issue de l'affichage d'une vidéo récompensée (#531). [EARNED] veut dire vue jusqu'au bout, pas
 * droit accordé : le droit vient du serveur (SSV), le contrôleur relit `/auth/me`.
 */
enum class RewardedOutcome { EARNED, DISMISSED, UNAVAILABLE }
